import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Building2,
  Trees,
  IdCard,
  Landmark,
  Calculator,
  ScrollText,
  Receipt,
  ArrowLeftRight,
} from "lucide-react";
import { useConsultation } from "../../context/ConsultationContext";
import { cn } from "../../lib/utils";
import MegaMenu from "./MegaMenu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const MOBILE_SERVICES = [
  { icon: Building2, label: "Mainland Setup", to: "/company-formation/mainland" },
  { icon: Trees, label: "Freezone Setup", to: "/company-formation/freezone" },
  { icon: IdCard, label: "Visa Services", to: "/visa" },
  { icon: Landmark, label: "Corporate Banking", to: "/banking" },
  { icon: Calculator, label: "Accounting", to: "/services/accounting" },
  { icon: ScrollText, label: "Corporate Tax", to: "/services/corporate-tax" },
  { icon: Receipt, label: "VAT", to: "/services/vat" },
  { icon: ArrowLeftRight, label: "Transfer Pricing", to: "/services/transfer-pricing" },
];

export default function HeaderV2() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);
  const { open } = useConsultation();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location]);

  // Hover-with-delay so users can move pointer onto the panel without it closing
  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), 140);
  };

  return (
    <header
      data-testid="v2-header"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        scrolled || mobileOpen
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      )}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="v2-logo" className="flex items-center">
          <img
            src={(scrolled || mobileOpen) ? "/brand/startinc-blue.png" : "/brand/startinc-onnavy.png"}
            alt="StartINC"
            className="h-10 md:h-11 w-auto select-none"
            draggable={false}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              cn(
                "font-heading text-sm tracking-wide transition-colors",
                scrolled ? "text-slate-700 hover:text-[#002366]" : "text-white/85 hover:text-white",
                isActive && (scrolled ? "text-[#002366]" : "text-white")
              )
            }
          >
            Home
          </NavLink>

          {/* Services mega menu trigger */}
          <div
            ref={triggerRef}
            className="relative"
            onMouseEnter={openMega}
            onMouseLeave={scheduleClose}
          >
            <button
              data-testid="v2-nav-services"
              onClick={() => setMegaOpen((s) => !s)}
              className={cn(
                "inline-flex items-center gap-1 font-heading text-sm tracking-wide transition-colors outline-none",
                scrolled ? "text-slate-700 hover:text-[#002366]" : "text-white/85 hover:text-white"
              )}
              aria-expanded={megaOpen}
            >
              Services
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-300",
                  megaOpen && "rotate-180"
                )}
              />
            </button>

            {megaOpen && (
              <MegaMenu
                onNavigate={() => setMegaOpen(false)}
                onMouseEnter={openMega}
                onMouseLeave={scheduleClose}
              />
            )}
          </div>

          {NAV.slice(1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "font-heading text-sm tracking-wide transition-colors",
                  scrolled ? "text-slate-700 hover:text-[#002366]" : "text-white/85 hover:text-white",
                  isActive && (scrolled ? "text-[#002366]" : "text-white")
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="v2-header-cta"
            onClick={() => open("other")}
            className="hidden md:inline-flex btn-gold text-sm"
          >
            Get In Touch
          </button>
          <button
            data-testid="v2-mobile-toggle"
            className={cn(
              "lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-sm border",
              (scrolled || mobileOpen)
                ? "border-slate-300 text-[#002366]"
                : "border-white/25 text-white"
            )}
            onClick={() => setMobileOpen((s) => !s)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="v2-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <div className="container-x py-5 flex flex-col gap-1">
            <Link
              to="/"
              className="font-heading text-slate-800 py-3 border-b border-slate-100"
            >
              Home
            </Link>
            <p className="overline text-[#3366FF] mt-3 mb-1">Services</p>
            {MOBILE_SERVICES.map((s) => {
              const I = s.icon;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  className="flex items-center gap-3 py-2.5 text-slate-800 border-b border-slate-100"
                >
                  <I className="w-4 h-4 text-[#3366FF]" />
                  <span className="font-heading text-sm">{s.label}</span>
                </Link>
              );
            })}
            <Link
              to="/about"
              className="font-heading text-slate-800 py-3 border-b border-slate-100 mt-3"
            >
              About
            </Link>
            <Link
              to="/blog"
              className="font-heading text-slate-800 py-3 border-b border-slate-100"
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="font-heading text-slate-800 py-3 border-b border-slate-100"
            >
              Contact
            </Link>
            <button
              data-testid="v2-mobile-cta"
              onClick={() => open("other")}
              className="btn-gold mt-5"
            >
              Get In Touch
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
